import * as React from 'react';
import { cn } from '@/lib/utils';

export interface CDLineNumberProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** The line number to display */
  number: number | string;
  /** Whether to pad single digits with leading zero */
  padZero?: boolean;
}

/**
 * CDLineNumber - Line number display for itemized rows
 *
 * Used in Closing Cost Details sections for numbered line items.
 * Displays as "01", "02", etc.
 *
 * Styling:
 * - Fixed 24px width
 * - Right-aligned
 * - 9pt Arial
 */
export const CDLineNumber = React.forwardRef<HTMLSpanElement, CDLineNumberProps>(
  ({ number, padZero = true, className, ...props }, ref) => {
    const displayNumber = React.useMemo(() => {
      const num = typeof number === 'string' ? Number.parseInt(number, 10) : number;
      if (isNaN(num)) return number.toString();
      return padZero && num < 10 ? `0${num}` : num.toString();
    }, [number, padZero]);

    return (
      <span ref={ref} className={cn('cd-line-number', className)} {...props}>
        {displayNumber}
      </span>
    );
  },
);

CDLineNumber.displayName = 'CDLineNumber';

/**
 * CDLineItem - A complete line item row with number, description, and amounts
 *
 * Common pattern in CD forms for itemized charges and credits.
 */
export interface CDLineItemProps {
  /** Line number */
  lineNumber: number | string;
  /** Description text */
  description: string;
  /** Optional sub-description or additional info */
  subDescription?: string;
  /** Amount for borrower column */
  borrowerAmount?: number | string;
  /** Amount for seller column */
  sellerAmount?: number | string;
  /** Amount for "Paid by Others" column */
  paidByOthersAmount?: number | string;
  /** Whether amounts are editable */
  editable?: boolean;
  /** onChange handler for amounts */
  onAmountChange?: (field: 'borrower' | 'seller' | 'paidByOthers', value: number) => void;
  /** Additional className for the row */
  className?: string;
}

export const CDLineItem = React.forwardRef<HTMLTableRowElement, CDLineItemProps>(
  (
    {
      lineNumber,
      description,
      subDescription,
      borrowerAmount,
      sellerAmount,
      paidByOthersAmount,
      editable = false,
      onAmountChange,
      className,
    },
    ref,
  ) => {
    const formatAmount = (value: number | string | undefined): string => {
      if (value === undefined || value === null || value === '') return '';
      const num = typeof value === 'string' ? Number.parseFloat(value) : value;
      if (isNaN(num)) return '';
      return num.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
    };

    return (
      <tr ref={ref} className={cn(className)}>
        <td className="cd-line-number">
          <CDLineNumber number={lineNumber} />
        </td>
        <td>
          <div className="cd-text-body">{description}</div>
          {subDescription && (
            <div className="cd-text-small text-cd-gray-text">{subDescription}</div>
          )}
        </td>
        {borrowerAmount !== undefined && (
          <td className="cd-cell-amount">
            {editable ? (
              <input
                type="text"
                className="cd-input cd-input-currency"
                value={formatAmount(borrowerAmount)}
                onChange={(e) => {
                  const val = Number.parseFloat(e.target.value.replace(/[,$]/g, ''));
                  if (!isNaN(val)) onAmountChange?.('borrower', val);
                }}
              />
            ) : (
              formatAmount(borrowerAmount)
            )}
          </td>
        )}
        {sellerAmount !== undefined && (
          <td className="cd-cell-amount">
            {editable ? (
              <input
                type="text"
                className="cd-input cd-input-currency"
                value={formatAmount(sellerAmount)}
                onChange={(e) => {
                  const val = Number.parseFloat(e.target.value.replace(/[,$]/g, ''));
                  if (!isNaN(val)) onAmountChange?.('seller', val);
                }}
              />
            ) : (
              formatAmount(sellerAmount)
            )}
          </td>
        )}
        {paidByOthersAmount !== undefined && (
          <td className="cd-cell-amount">
            {editable ? (
              <input
                type="text"
                className="cd-input cd-input-currency"
                value={formatAmount(paidByOthersAmount)}
                onChange={(e) => {
                  const val = Number.parseFloat(e.target.value.replace(/[,$]/g, ''));
                  if (!isNaN(val)) onAmountChange?.('paidByOthers', val);
                }}
              />
            ) : (
              formatAmount(paidByOthersAmount)
            )}
          </td>
        )}
      </tr>
    );
  },
);

CDLineItem.displayName = 'CDLineItem';
